import { useQuery } from "@apollo/client";
import { Box, MenuItem, Typography } from "@mui/material";
import {
  Form,
  Formik,
  FormikHelpers,
  FormikTouched,
  useField,
  useFormikContext,
} from "formik";
import { useConfirm } from "material-ui-confirm";
import { enqueueSnackbar } from "notistack";
import { ReactElement, ReactNode } from "react";

import {
  CampaignStatus,
  EditCampaignMessagingStrategyMutation,
  EditCampaignMessagingStrategyMutationVariables,
  ListActiveOwnedPhoneNumbersQuery,
  ListActiveOwnedPhoneNumbersQueryVariables,
  ListOwnedPhoneNumbersQuery,
  MessagingStrategyName,
  OwnedPhoneNumberPoolType,
  PhoneNumberMessageClass,
  ProviderName,
  ResetCampaignContactImportJobMutation,
  ResetCampaignContactImportJobMutationVariables,
  SingleSendPhoneNumberFieldsFragment,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import { FlexBox } from "~frontend/components/FlexBox";
import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import Select, { FormikSelect } from "~frontend/components/forms/FormSelect";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { VALID_PERSONALIZED_GIF_MESSAGING_STRATEGIES } from "~frontend/components/personalizedGifs/types";
import {
  EDIT_CAMPAIGN_MESSAGING_STRATEGY,
  RESET_CAMPAIGN_CONTACT_UPLOAD_JOB,
} from "~frontend/graphql/campaigns";
import { LIST_ACTIVE_OWNED_PHONE_NUMBERS } from "~frontend/graphql/ownedPhoneNumber";
import { SubaccountAdminData } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import { NumberStatusTag } from "~frontend/routes/subaccounts/phoneNumberList/PhoneNumberTable";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import { prettyPhoneNumber } from "~frontend/util/phoneNumber";
import useMutation from "~frontend/util/useMutation";

import { StepInstructions } from "./StepInstructions";
import { FormikSaveButtons } from "./campaignEditorUtils/SaveButtons";
import { CampaignForEditor } from "./types";

export type OwnedPhoneNumber =
  ListOwnedPhoneNumbersQuery["listOwnedPhoneNumbers"][0];

export type OwnedPhoneNumberPool =
  SubaccountAdminData["ownedPhoneNumberPools"][0];

type ActiveOwnedPhoneNumber =
  ListActiveOwnedPhoneNumbersQuery["listActiveOwnedPhoneNumbers"][number];

function OwnedPhoneNumberPicker({
  readOnly,
  name,
  ownedPhoneNumbers,
}: {
  readOnly?: boolean;
  name: string;
  ownedPhoneNumbers: ActiveOwnedPhoneNumber[];
}): ReactElement {
  const formik = useFormikContext<Values>();

  // It's possible the owned phone number was archived and won't show
  // up in the active numbers list, so we need to provide a dummy
  // element in that case. Archived pools DO show up in
  // subaccount.ownedPhoneNumberPools, so the pools list doesn't have
  // the same problem.
  const selectedNumberId =
    formik.values.messagingStrategy.config.ownedPhoneNumberId;

  const configuredButInvalidElement = ownedPhoneNumbers.find(
    pn => pn.id === selectedNumberId
  ) ? undefined : (
    <MenuItem key={selectedNumberId} value={selectedNumberId} disabled>
      (unavailable)
    </MenuItem>
  );

  return (
    <FormikSelect
      name={name}
      fullWidth
      label="Phone Number"
      disabled={readOnly}
    >
      {configuredButInvalidElement}
      {ownedPhoneNumbers.map(phoneNumber => {
        const prettyNumber = prettyPhoneNumber(phoneNumber.phoneNumber);

        const formattedName = phoneNumber.displayName
          ? `${phoneNumber.displayName} - ${prettyNumber}`
          : prettyNumber;
        const isOptInNumber = !!phoneNumber.optInGroup?.id;

        return (
          <MenuItem key={phoneNumber.id} value={phoneNumber.id}>
            <FlexBox direction="row" alignItems={"center"}>
              {formattedName}
              {isOptInNumber && (
                <NumberStatusTag
                  backgroundColor={palette.lightOrange}
                  color={palette.navy}
                  sx={{ ml: 1 }}
                >
                  Opt In
                </NumberStatusTag>
              )}
            </FlexBox>
          </MenuItem>
        );
      })}
    </FormikSelect>
  );
}

function OwnedPhoneNumberPoolPicker({
  readOnly,
  name,
  ownedPhoneNumberPools,
}: {
  readOnly?: boolean;
  name: string;
  ownedPhoneNumberPools: OwnedPhoneNumberPool[];
}): ReactElement {
  const [field] = useField<string>(name);

  const availablePools = ownedPhoneNumberPools.filter(p => !p.archivedAt);
  const currentPoolId = field.value;

  let currentButInvalidPool: OwnedPhoneNumberPool | undefined = undefined;
  if (currentPoolId && !availablePools.find(p => p.id === currentPoolId)) {
    currentButInvalidPool = ownedPhoneNumberPools.find(
      p => p.id === currentPoolId
    );
  }

  return (
    <FormikSelect
      name={name}
      fullWidth
      label="Phone Number Pool"
      disabled={readOnly}
    >
      {currentButInvalidPool && (
        <MenuItem disabled value={currentButInvalidPool.id}>
          {currentButInvalidPool.name} (not available)
        </MenuItem>
      )}

      {availablePools.map(phoneNumberPool => {
        return (
          <MenuItem key={phoneNumberPool.id} value={phoneNumberPool.id}>
            {phoneNumberPool.name}
          </MenuItem>
        );
      })}
    </FormikSelect>
  );
}

type Values = {
  [submitErrorField]?: string | null;
  goToNext?: boolean;
  messagingStrategy: {
    strategy: MessagingStrategyName;
    config: any;
  };
};

type SingleSendMessagingStrategies = {
  config: {
    ownedPhoneNumber: SingleSendPhoneNumberFieldsFragment;
  };
} & CampaignForEditor["messagingStrategy"];

const MESSAGING_STRATEGY_DISPLAY_NAMES = {
  [MessagingStrategyName.Fake]: "Fake",
  [MessagingStrategyName.Fake_10Dlc]: "Fake 10DLC",
  [MessagingStrategyName.FakePool]: "Fake Pool",
  [MessagingStrategyName.FakeShortCode]: "Fake Short Code",
  [MessagingStrategyName.FakeTollFree]: "Fake Toll Free",
  [MessagingStrategyName.Bandwidth_10Dlc]: "Bandwidth 10DLC",
  [MessagingStrategyName.Bandwidth_10DlcPool]: "Bandwidth 10DLC MMS Pool",
  [MessagingStrategyName.BandwidthShortCode]: "Bandwidth Short Code",
  [MessagingStrategyName.BandwidthTollFree]: "Bandwidth Toll Free",
  [MessagingStrategyName.BandwidthUnregisteredPool]:
    "Bandwidth Unregistered Pool",
  [MessagingStrategyName.Sinch_10Dlc]: "Sinch 10DLC",
  [MessagingStrategyName.TwilioShortCode]: "Twilio Short Code",
} as const;

function getInitialValues(
  campaign: Pick<CampaignForEditor, "messagingStrategy">
): Values {
  switch (campaign.messagingStrategy.__typename) {
    case "CampaignMessagingStrategyBandwidth10DlcPool":
    case "CampaignMessagingStrategyBandwidthUnregisteredPool":
    case "CampaignMessagingStrategyFakePool":
      return {
        messagingStrategy: {
          strategy: campaign.messagingStrategy.strategy,
          config: {
            ownedPhoneNumberPoolId:
              campaign.messagingStrategy.config.ownedPhoneNumberPool.id,
          },
        },
      };

    case "CampaignMessagingStrategyFake":
      return {
        messagingStrategy: {
          strategy: campaign.messagingStrategy.strategy,
          config: {},
        },
      };

    default:
      return {
        messagingStrategy: {
          strategy: campaign.messagingStrategy.strategy,
          config: {
            ownedPhoneNumberId:
              campaign.messagingStrategy.config.ownedPhoneNumber.id,
          },
        },
      };
  }
}

function messagingStrategyNameForPhoneNumber(
  opn: ActiveOwnedPhoneNumber
): MessagingStrategyName {
  const provider = opn.provider;
  const messageClass = opn.messageClass;
  switch (provider) {
    case ProviderName.Bandwidth: {
      switch (messageClass) {
        case PhoneNumberMessageClass.TenDlc:
          return MessagingStrategyName.Bandwidth_10Dlc;
        case PhoneNumberMessageClass.ShortCode:
          return MessagingStrategyName.BandwidthShortCode;
        case PhoneNumberMessageClass.TollFree:
          return MessagingStrategyName.BandwidthTollFree;
        default:
          throw new Error(
            "No messaging strategy available for message class and provider combo"
          );
      }
    }
    case ProviderName.Twilio: {
      switch (messageClass) {
        case PhoneNumberMessageClass.ShortCode:
          return MessagingStrategyName.TwilioShortCode;
        default:
          throw new Error(
            "No messaging strategy available for message class and provider combo"
          );
      }
    }
    case ProviderName.Sinch: {
      switch (messageClass) {
        case PhoneNumberMessageClass.TenDlc:
          return MessagingStrategyName.Sinch_10Dlc;
        default:
          throw new Error(
            "No messaging strategy available for message class and provider combo"
          );
      }
    }
    case ProviderName.Fake: {
      switch (messageClass) {
        case PhoneNumberMessageClass.TenDlc:
          return MessagingStrategyName.Fake_10Dlc;
        case PhoneNumberMessageClass.ShortCode:
          return MessagingStrategyName.FakeShortCode;
        case PhoneNumberMessageClass.TollFree:
          return MessagingStrategyName.FakeTollFree;
        default:
          throw new Error(
            "No messaging strategy available for message class and provider combo"
          );
      }
    }
    default:
      throw new Error("Unknown provider");
  }
}

function messagingStrategyNameForPoolType(
  poolType: OwnedPhoneNumberPoolType
): MessagingStrategyName {
  switch (poolType) {
    case OwnedPhoneNumberPoolType.BandwidthUnregistered:
      return MessagingStrategyName.BandwidthUnregisteredPool;
    case OwnedPhoneNumberPoolType.Fake:
      return MessagingStrategyName.FakePool;
    case OwnedPhoneNumberPoolType.TenDlcMms:
      return MessagingStrategyName.Bandwidth_10DlcPool;
  }
}

export default function SendingNumbers({
  campaign,
  goToNextSection,
}: {
  campaign: CampaignForEditor;
  goToNextSection: (opts?: { bypassRouteLeavingGuard?: boolean }) => void;
}): ReactElement {
  const subaccount = useSubaccountAdminConfig()!;
  const confirm = useConfirm();
  const formatError = useErrorMessageFormatter();

  const [editCampaignMessagingStrategy] = useMutation<
    EditCampaignMessagingStrategyMutation,
    EditCampaignMessagingStrategyMutationVariables
  >(EDIT_CAMPAIGN_MESSAGING_STRATEGY);

  const [resetCampaignContactImportJob] = useMutation<
    ResetCampaignContactImportJobMutation,
    ResetCampaignContactImportJobMutationVariables
  >(RESET_CAMPAIGN_CONTACT_UPLOAD_JOB);

  const campaignHasPersonalizedGifs =
    campaign.scriptVariants.filter(
      variant => variant.personalizedGif !== null && variant.deletedAt === null
    ).length > 0;

  const { loading, data, error } = useQuery<
    ListActiveOwnedPhoneNumbersQuery,
    ListActiveOwnedPhoneNumbersQueryVariables
  >(LIST_ACTIVE_OWNED_PHONE_NUMBERS, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
  });

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  if (loading) {
    return (
      <Select value="" disabled>
        <MenuItem value="">Loading...</MenuItem>
      </Select>
    );
  }

  let availableNumbersForSelect = data!.listActiveOwnedPhoneNumbers;
  let cannotChangeNumber = false;

  if (campaign.startedAt && campaign.usingOptInStrategy) {
    const {
      optInGroup,
      messageClass,
      id: ownedPhoneNumberId,
    } = (campaign.messagingStrategy as SingleSendMessagingStrategies).config
      .ownedPhoneNumber;
    const optInGroupId = optInGroup!.id;

    if (messageClass === PhoneNumberMessageClass.ShortCode) {
      // If a short code is already selected, they cannot select other options
      availableNumbersForSelect = availableNumbersForSelect.filter(
        number => number.id === ownedPhoneNumberId
      );
    } else {
      // If it's not a short code, and a long code opt-in, allow the user
      // to select available short codes.
      availableNumbersForSelect = availableNumbersForSelect.filter(
        number => number.optInGroup?.id === optInGroupId
      );
    }
    cannotChangeNumber =
      availableNumbersForSelect.length === 1 &&
      ownedPhoneNumberId === availableNumbersForSelect[0].id;
  }

  if (campaign.startedAt && !campaign.usingOptInStrategy) {
    // Don't allow users to switch to opt in strategies once started.
    availableNumbersForSelect = availableNumbersForSelect.filter(
      number => !number.optInGroup
    );
  }

  const numbersByStrategy: {
    [key in MessagingStrategyName]?: ActiveOwnedPhoneNumber[];
  } = {};

  for (const number of availableNumbersForSelect) {
    const strategy: MessagingStrategyName =
      messagingStrategyNameForPhoneNumber(number);

    if (!numbersByStrategy[strategy]) {
      numbersByStrategy[strategy] = [number];
    } else {
      numbersByStrategy[strategy].push(number);
    }
  }

  const poolsByStrategy: {
    [key in MessagingStrategyName]?: OwnedPhoneNumberPool[];
  } = {};

  if (!(campaign.startedAt && campaign.usingOptInStrategy)) {
    for (const pool of subaccount.ownedPhoneNumberPools) {
      const strategy = messagingStrategyNameForPoolType(pool.type);
      if (!poolsByStrategy[strategy]) {
        poolsByStrategy[strategy] = [pool];
      } else {
        poolsByStrategy[strategy].push(pool);
      }
    }
  }

  const onValidate = (values: Values) => {
    const errors: {
      messagingStrategy: {
        strategy: string | undefined;
        config: {
          ownedPhoneNumberId: string | undefined;
          ownedPhoneNumberPoolId: string | undefined;
        };
      };
    } = {
      messagingStrategy: {
        strategy: undefined,
        config: {
          ownedPhoneNumberId: undefined,
          ownedPhoneNumberPoolId: undefined,
        },
      },
    };

    if (!availableStrategies.includes(values.messagingStrategy.strategy)) {
      errors.messagingStrategy.strategy = "Strategy is no longer available";
    } else if (
      campaignHasPersonalizedGifs &&
      !VALID_PERSONALIZED_GIF_MESSAGING_STRATEGIES.includes(
        values.messagingStrategy.strategy
      )
    ) {
      errors.messagingStrategy.strategy =
        "Must select Bandwidth phone numbers when scripts use personalized gifs";
    }

    if (
      values.messagingStrategy.config.ownedPhoneNumberId === "" ||
      (values.messagingStrategy.config.ownedPhoneNumberId &&
        !data?.listActiveOwnedPhoneNumbers.find(
          number =>
            number.id === values.messagingStrategy.config.ownedPhoneNumberId
        ))
    ) {
      errors.messagingStrategy.config.ownedPhoneNumberId =
        "Currently configured phone number is no longer available";
    }

    if (values.messagingStrategy.config.ownedPhoneNumberPoolId) {
      const pool = subaccount.ownedPhoneNumberPools.find(
        pool =>
          pool.id === values.messagingStrategy.config.ownedPhoneNumberPoolId
      );

      if (!pool || pool.archivedAt) {
        errors.messagingStrategy.config.ownedPhoneNumberPoolId =
          "Currently configured pool is no longer available";
      }
    }

    // the SaveButtons component checks Object.keys(errors) to disable
    // itself so we need to only return a populated object if there is
    // actually an error
    if (
      errors.messagingStrategy.strategy ||
      errors.messagingStrategy.config.ownedPhoneNumberId ||
      errors.messagingStrategy.config.ownedPhoneNumberPoolId
    ) {
      return errors;
    } else {
      return {};
    }
  };

  async function resetStagedCampaignContacts() {
    if ((campaign.contactImportJob?.result?.nContacts ?? 0) > 0) {
      const { confirmed } = await confirm({
        title: "Confirm clear contacts",
        description: "Are you sure? This will delete any existing contacts.",
      });
      if (!confirmed) {
        return false;
      }
    }

    const { errors } = await resetCampaignContactImportJob({
      variables: {
        input: {
          campaignId: campaign.id,
        },
      },
    });

    if (errors) {
      return false;
    }
    return true;
  }

  const handleSave = async (
    { messagingStrategy, goToNext }: Values,
    { setSubmitting, resetForm }: FormikHelpers<Values>
  ) => {
    if (campaign.usingOptInStrategy) {
      const newOpnId = messagingStrategy.config.ownedPhoneNumberId;
      const newConfig = availableNumbersForSelect.find(
        number => number.id === newOpnId
      )!;
      const newOptInGroupId = newConfig?.optInGroup?.id;
      const currentOptInGroupId = (
        campaign.messagingStrategy as SingleSendMessagingStrategies
      ).config.ownedPhoneNumber.optInGroup!.id;

      if (newOptInGroupId !== currentOptInGroupId) {
        // If we're using a different opt-in group or going to non-opt-in,
        // we need to reset the contacts.
        const confirmed = await resetStagedCampaignContacts();

        if (!confirmed) {
          return;
        }
      }
    } else if (messagingStrategy.config.ownedPhoneNumberId) {
      const newOpnId = messagingStrategy.config.ownedPhoneNumberId;
      const newConfig = availableNumbersForSelect.find(
        number => number.id === newOpnId
      )!;
      if (newConfig.optInGroup) {
        const confirmed = await resetStagedCampaignContacts();

        if (!confirmed) {
          return;
        }
      }
    }

    const { errors, data } = await editCampaignMessagingStrategy({
      variables: {
        input: {
          campaignId: campaign.id,
          messagingStrategyName: messagingStrategy.strategy,
          ownedPhoneNumberId: messagingStrategy.config.ownedPhoneNumberId,
          ownedPhoneNumberPoolId:
            messagingStrategy.config.ownedPhoneNumberPoolId,
        },
      },
    });

    setSubmitting(false);

    if (errors) {
      enqueueSnackbar(`Failed to save campaign: ${formatError(errors)}`, {
        variant: "error",
      });
    } else {
      resetForm({
        values: getInitialValues(data!.editCampaignMessagingStrategy),
      });

      if (goToNext) {
        goToNextSection({ bypassRouteLeavingGuard: true });
      } else {
        enqueueSnackbar("Saved campaign", { variant: "info" });
      }
    }
  };

  const readOnly =
    [
      CampaignStatus.InitialSendRunning,
      CampaignStatus.InitialSendCanceling,
      CampaignStatus.InitialSendComplete,
      CampaignStatus.Archived,
    ].includes(campaign.status) || cannotChangeNumber;

  // Mapping over the keys of MESSAGING_STRATEGY_DISPLAY_NAMES to render the list of options stabilizes the display order.
  const availableStrategies = Object.keys(
    MESSAGING_STRATEGY_DISPLAY_NAMES
  ).filter(
    strategyName =>
      numbersByStrategy[strategyName as MessagingStrategyName] ||
      poolsByStrategy[strategyName as MessagingStrategyName]?.find(
        pool => !pool.archivedAt
      )
  );

  return (
    <>
      <Box sx={{ pb: 2 }}>
        <StepInstructions header="Choose a phone number for sending texts" />
      </Box>
      <Formik
        initialValues={getInitialValues(campaign)}
        initialTouched={{
          messagingStrategy: {
            strategy: true,
            config: {
              ownedPhoneNumberId: true,
              ownedPhoneNumberPoolId: true,
            },
          },
        }}
        validate={onValidate}
        validateOnMount={true}
        onSubmit={handleSave}
      >
        {props => {
          const handleChangeType = async (
            event: React.ChangeEvent<{ value: unknown }>
          ) => {
            const strategy = event.target.value as MessagingStrategyName;

            if (numbersByStrategy[strategy]) {
              const newDefaultNumber = numbersByStrategy[strategy][0];
              props
                .setFieldValue("messagingStrategy.config", {
                  ownedPhoneNumberId: newDefaultNumber?.id ?? "",
                })
                .then()
                .catch(console.error);

              props
                .setTouched({
                  "messagingStrategy.config.ownedPhoneNumberId": true,
                } as FormikTouched<Values>)
                .catch(console.error);
            } else {
              const pool = poolsByStrategy[strategy]![0];
              props
                .setFieldValue("messagingStrategy.config", {
                  ownedPhoneNumberPoolId: pool?.id ?? "",
                })
                .catch(console.error);

              if (!pool) {
                console.error("Could not find number or pool");
                return;
              }
            }

            props.handleChange(event);
          };

          const currentStrategy = props.values.messagingStrategy.strategy;
          return (
            <Form>
              <RouteLeavingGuard when={props.dirty} />
              <FormikErrorBanner />
              {cannotChangeNumber && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The sending number on a shortcode campaign cannot be changed
                  after the campaign has started.
                </Typography>
              )}
              <Select
                fullWidth
                disabled={readOnly}
                label="Phone Number Type"
                name="messagingStrategy.strategy"
                onChange={handleChangeType}
                value={props.values.messagingStrategy.strategy}
                onBlur={props.handleBlur}
                error={!!props.errors.messagingStrategy?.strategy}
                helperText={props.errors.messagingStrategy?.strategy}
              >
                {availableStrategies.map(strategyName => (
                  <MenuItem key={strategyName} value={strategyName}>
                    {
                      MESSAGING_STRATEGY_DISPLAY_NAMES[
                        strategyName as MessagingStrategyName
                      ]
                    }
                  </MenuItem>
                ))}
              </Select>
              {props.values.messagingStrategy.strategy in poolsByStrategy ? (
                <OwnedPhoneNumberPoolPicker
                  readOnly={readOnly}
                  name="messagingStrategy.config.ownedPhoneNumberPoolId"
                  ownedPhoneNumberPools={poolsByStrategy[currentStrategy]!}
                />
              ) : (
                <OwnedPhoneNumberPicker
                  readOnly={readOnly}
                  name="messagingStrategy.config.ownedPhoneNumberId"
                  ownedPhoneNumbers={
                    // If the configured strategy is not available, it
                    // might not be present in poolsByStrategy either,
                    // so we can't use ! here.
                    numbersByStrategy[currentStrategy] || []
                  }
                />
              )}

              <FormikSaveButtons continueWithoutSaving={goToNextSection} />
            </Form>
          );
        }}
      </Formik>
    </>
  );
}

export function validateSendingNumbers({
  campaign,
  subaccount,
}: {
  campaign: CampaignForEditor;
  subaccount: SubaccountAdminData;
}): { title: string; message: ReactNode }[] {
  const config = campaign.messagingStrategy.config as any;
  if (config.ownedPhoneNumber) {
    // This function is synchronous so we can't actually check whether the number exists.
    // The backend will have to validate that if something weird happened.
    return [];
  } else if (config.ownedPhoneNumberPool) {
    const pool = subaccount.ownedPhoneNumberPools.find(
      pool => pool.id === config.ownedPhoneNumberPool.id && !pool.archivedAt
    );

    if (pool) {
      return [];
    } else {
      return [
        {
          title: "Sending Numbers are invalid",
          message:
            "You must change your Sending Numbers before starting your campaign.",
        },
      ];
    }
    // validate pool is active
  } else {
    return [
      {
        title: "Error validating sending numbers",
        message: "Don't know how to validate configured messaging strategy",
      },
    ];
  }
}
